import { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../ui/noData/NoData';
import { useTranslation } from 'react-i18next';
import getCurrentRoute from '../../lib/helper-functions/getCurrentRoute';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import get_checkout_tracking_api_call from '../../lib/api/integration/get_checkout_tracking_api_call';
import post_integration_api_call from '../../lib/api/integration/post_integration_api_call';
import { allRoutes } from '../../lib/constants/route';
import { settingSVG } from '../../lib/allCustomSVGs';
import { submitLogToBugsnag } from '../../lib/api/log';
import { asyncHandler } from '../../lib/api/async-handler';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { HELP_LINE_API } from '../../lib/constants/central-api';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { setMessage } from '../../store/reducers/appSlice';
import CssClasses from './checkoutTrackingSections/CssClasses/CssClasses';
import Autofill from './checkoutTrackingSections/Autofill/Autofill';
import Integration from './checkoutTrackingSections/Integration/Integration';
import EventTracking from './checkoutTrackingSections/CustomEvent/EventTracking';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import update_mcp_api_call from '../../lib/api/mcp/update-mcp';
import get_all_connected_mcp_api_call from '../../lib/api/mcp/get-all-connected-mcp';

interface ApiDataType {
  api_key: string;
  [key: string]: any;
}

function CheckoutTrackingPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('null');
  const [key, setKey] = useState<string>('');
  const [refetchTrackingDataRequired, setRefetchTrackingDataRequired] =
    useState<boolean>(false);
  const [isLoadingConnection, setIsLoadingConnection] = useState<boolean>(true);
  const [apiData, setAPIData] = useState<ApiDataType | null>(null);
  const [isAutofillEnabled, setIsAutofillEnabled] = useState<boolean>(false);
  const [isTrackerON, setIsTrackerON] = useState<boolean>(true);
  const [isTrackingScriptConnected, setIsTrackingScriptConnected] =
    useState<boolean>(false);
  const [optimizeValue, setOptimizeValue] = useState<number>(0);
  const [mcpConnectionId, setMcpConnectionId] = useState<number | null>(null);
  const [mcpIsActive, setMcpIsActive] = useState<boolean | null>(null);
  const shopURL = useSelector(
    (state: RootState) => state.user?.company?.webshop_url
  );
  const shopSlug = useSelector(
    (state: RootState) => state.user?.company?.shop_slug
  );
  const accessToken = useSelector(
    (state: RootState) => state.auth.authorization.accessToken
  );
  const isWhiteLabel = useSelector(
    (state: RootState) => state.UI?.isWhiteLabel
  );

  let sellerToken: string;
  if (isWhiteLabel) {
    sellerToken = accessToken?.currentWhiteLabelSellerToken;
  } else {
    sellerToken = accessToken?.sellerToken;
  }
  const { t } = useTranslation();

  const location = useLocation();
  const currentRoute = getCurrentRoute(location);

  useEffect(() => {
    if (!isNotEmptyString(key)) {
      getAvailableTrackingData();
    }
  }, [key]);

  useEffect(() => {
    if (isNotEmptyString(key) && currentRoute === 'integration') {
      getAvailableTrackingData();
      setRefetchTrackingDataRequired(false);
    }
  }, [currentRoute]);

  useEffect(() => {
    if (currentRoute !== 'integration') {
      setRefetchTrackingDataRequired(true);
    }
  }, [currentRoute]);

  useEffect(() => {
    //* Retrieve connection_id and is_active values of "AdUp AI Tracking" MCP from the URL parameters
    const urlParams = new URLSearchParams(window.location.search);

    if (
      isNotEmptyString(urlParams.get('connection_id')) &&
      isNotEmptyString(urlParams.get('is_active'))
    ) {
      const connectionId: number | null = Number(
        urlParams.get('connection_id')
      );
      const isActive: boolean | null = urlParams.get('is_active') === 'true';
      setMcpConnectionId(connectionId);
      setMcpIsActive(isActive);
      //* Removing parameters from browser URL
      window.history.replaceState({}, '', window?.location?.pathname);
    } else {
      const connectedMCPs = get_all_connected_mcp_api_call();
      connectedMCPs
        .then((connectedMCPs: []) => {
          const adUpAITrackingMCP: any = connectedMCPs.find(
            (item: any) =>
              item?.tara_mcp && item?.tara_mcp?.slug === 'adup_ai_tracking'
          );
          setMcpConnectionId(adUpAITrackingMCP?.id);
          setMcpIsActive(adUpAITrackingMCP?.is_active);
        })
        .catch((error) => {
          console.log('Error fetching connected MCPs:', error);
        });
    }
  }, []);

  useEffect(() => {
    if (
      key &&
      loading === 'success' &&
      !isLoadingConnection &&
      !refetchTrackingDataRequired &&
      mcpIsActive !== isTrackingScriptConnected &&
      mcpConnectionId
    ) {
      //* Update "AdUp AI Tracking" MCP connection
      updateMCP(mcpConnectionId, isTrackingScriptConnected);
    }
  }, [
    key,
    loading,
    isLoadingConnection,
    refetchTrackingDataRequired,
    mcpIsActive,
    isTrackingScriptConnected,
  ]);

  const updateMCP = async (mcpConnectionId: number, isActive: boolean) => {
    try {
      const response = await update_mcp_api_call(
        mcpConnectionId,
        {},
        isActive,
        undefined,
        false
      );
      if (response?.success) {
        setMcpIsActive(isActive);
      }
    } catch (error) {
      console.error('Error updating MCP:', error);
      submitLogToBugsnag('error', `Error in updating MCP: ${error}`);
    }
  };

  const getAvailableTrackingData = async () => {
    setLoading('pending');
    try {
      const getRes: ApiDataType = await get_checkout_tracking_api_call();

      if (!getRes || Object.keys(getRes).length === 0) {
        const sendingData = {
          name: 'checkout-tracking-class-names',
          type: 'tracker',
          configuration: { key: 'value' },
        };
        const postRes = await post_integration_api_call(
          sendingData,
          undefined,
          undefined,
          undefined,
          undefined
        );
        if (postRes && postRes.api_key) {
          setKey(postRes.api_key);
          setAPIData(postRes);
        }
      } else {
        setAPIData(getRes);
        if (Object.keys(getRes).length > 0) {
          const additionalConfigs = getRes?.additional_configs;

          if (typeof additionalConfigs?.is_autofill_enabled === 'string') {
            setIsAutofillEnabled(
              additionalConfigs?.is_autofill_enabled === 'true' ? true : false
            );
          } else {
            setIsAutofillEnabled(false);
          }

          if (typeof additionalConfigs?.is_tracker_active === 'string') {
            setIsTrackerON(
              additionalConfigs?.is_tracker_active === 'false' ? false : true
            );
          } else {
            setIsTrackerON(true);
          }

          if (
            typeof additionalConfigs?.optimization_adjustment_ratio === 'number'
          ) {
            setOptimizeValue(additionalConfigs?.optimization_adjustment_ratio);
          } else {
            setOptimizeValue(0);
          }
        }
        setKey(getRes?.api_key || '');
      }
      setLoading('success');
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setLoading('error');
    }
  };

  /* ================================================== */
  const onSubmitHandler = async () => {
    if (!isNotEmptyString(shopURL)) {
      const message = t(
        'ShopURLNotAvailablePleaseUpdateYourShopURLThroughSettings'
      );
      const messageType = 'warning';
      dispatch(setMessage({ message, messageType }));
      return;
    }

    try {
      const sendingData = JSON.stringify({
        type: 'tracking',
        data: {
          cms_url: shopURL,
          shop_slug: shopSlug,
        },
      });

      const requestInfo: RequestInfo = {
        url: HELP_LINE_API,
        method: 'POST',
        body: sendingData,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'HELP_LINE_API_POST';
      const customMsg = t('EmailSentSuccessfully');

      const res: any = await asyncHandler(requestInfo, actionType, customMsg);

      const response = res.data;
      if (res.actionType === actionType && response.success) {
      }
    } catch (exception) {
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  };
  /* ================================================== */

  const title =
    currentRoute === 'css'
      ? t('CSSTracking')
      : currentRoute === 'integration'
      ? t('Integration')
      : currentRoute === 'autofill'
      ? t('Autofill')
      : currentRoute === 'events'
      ? t('EventTracking')
      : currentRoute === 'trends'
      ? t('Recommendations')
      : t('CheckoutTracking');

  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={title ? title : t('CheckoutTracking')}
            mainBtnIcon={
              currentRoute === 'css' || currentRoute === 'events'
                ? settingSVG
                : undefined
            }
            mainBtnTitle={
              currentRoute === 'css' || currentRoute === 'events'
                ? t('SetupForMe')
                : undefined
            }
            mainBtnAction={
              currentRoute === 'css' || currentRoute === 'events'
                ? () => {
                    onSubmitHandler();
                  }
                : undefined
            }
            // subTitle={subTitle}
            backBtnLink={'/integrations'}
          >
            {loading === 'pending' && <LoadingSpinner />}
            {loading === 'error' && (
              <NoData
                title={t('ErrorFetchingData')}
                subTitle={t('SomethingWentWrong-msg')}
              />
            )}

            <Routes>
              {allRoutes['/tracking/css'] && (
                <Route
                  path='css/*'
                  element={
                    loading === 'success' ? (
                      <CssClasses
                        data={apiData}
                        setAPIData={setAPIData}
                        apiKey={key}
                        setScriptKey={setKey}
                        isAutofillEnabled={isAutofillEnabled}
                        isTrackerON={isTrackerON}
                        optimizeValue={optimizeValue}
                      />
                    ) : null
                  }
                />
              )}
              {allRoutes['/tracking/autofill'] && (
                <Route
                  path='autofill/*'
                  element={
                    loading === 'success' ? (
                      <Autofill
                        setIsAutofillEnabled={setIsAutofillEnabled}
                        isAutofillEnabled={isAutofillEnabled}
                        isTrackerON={isTrackerON}
                        optimizeValue={optimizeValue}
                        data={apiData}
                        setAPIData={setAPIData}
                        setScriptKey={setKey}
                      />
                    ) : null
                  }
                />
              )}
              {allRoutes['/tracking/integration'] && (
                <Route
                  path='integration/*'
                  element={
                    key &&
                    !refetchTrackingDataRequired &&
                    loading === 'success' ? (
                      <Integration
                        apiKey={key}
                        isTrackerON={isTrackerON}
                        setIsTrackerON={setIsTrackerON}
                        isTrackingScriptConnected={isTrackingScriptConnected}
                        setIsTrackingScriptConnected={
                          setIsTrackingScriptConnected
                        }
                        optimizeValue={optimizeValue}
                        setOptimizeValue={setOptimizeValue}
                        isAutofillEnabled={isAutofillEnabled}
                        data={apiData}
                        setAPIData={setAPIData}
                        setScriptKey={setKey}
                        isLoadingConnection={isLoadingConnection}
                        setIsLoadingConnection={setIsLoadingConnection}
                      />
                    ) : null
                  }
                />
              )}
              {allRoutes['/tracking/events'] && (
                <Route
                  path='events/*'
                  element={
                    loading === 'success' ? (
                      <EventTracking
                        apiKey={key}
                        data={apiData}
                        setAPIData={setAPIData}
                        setScriptKey={setKey}
                        isAutofillEnabled={isAutofillEnabled}
                        isTrackerON={isTrackerON}
                        optimizeValue={optimizeValue}
                      />
                    ) : null
                  }
                />
              )}
            </Routes>
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
}

export default CheckoutTrackingPage;
